import {
  type CopyableStore,
  CorePasteAction,
  Factory,
  type ID,
  type NodeVariant,
  type Notes,
  ProjectActions,
  selectCompleteChildIds,
  selectEntityIndex,
  selectNodeChildren,
  selectNodeShallowChildIds,
  selectSiblingParents,
  selectStoreToCopy,
} from '@octopus-do/octopus-project-core'
import type { AppThunk } from '@store/migrationActions/thunkActions/types'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { getId } from '@store/utils'
import { commitProjectRequest } from '@storeOld/actionCreators'
import * as ActionTypes from '@storeOld/actions'
import { CanvasActionTypes } from '@storeOld/reducers/canvas/canvas'

import type { CopySelectNodeArgs } from '@/types/nodes'
import { encodeAndCopyToClipboard } from '@/utils/clipboard'

export const thunkAddNodeSibling =
  (targetId: ID): AppThunk =>
  (dispatch, getState) => {
    const colorId = migrationProjectSelector.getProjectDefaultColorId(
      getState()
    )

    const parents = selectSiblingParents(targetId)(getState())
    const parent = parents.at(0)

    if (!parent) {
      return
    }

    const index = selectEntityIndex(targetId)(getState())

    const node = Factory.node({
      id: getId(),
      colorId,
    })

    dispatch(
      commitProjectRequest(
        ProjectActions.nodes.add({
          items: [node],
          index: index + 1,
          parents: [parent],
        })
      )
    )
  }

export const thunkAddNodeChildren =
  (parentId: ID): AppThunk =>
  (dispatch, getState) => {
    const colorId = migrationProjectSelector.getProjectDefaultColorId(
      getState()
    )

    const children = selectNodeChildren(parentId)(getState())
    const index = children.length

    const node = Factory.node({
      id: getId(),
      colorId,
    })

    dispatch({
      type: ActionTypes.COMMIT_PROJECT_REQUEST,
      payload: [
        ProjectActions.nodes.add({
          items: [node],
          index,
          parents: [parentId],
        }),
      ],
    })
  }

export const thunkDuplicateNode =
  (id: ID): AppThunk =>
  (dispatch) => {
    const initiator = getId()

    dispatch({
      type: ActionTypes.COMMIT_PROJECT_REQUEST,
      payload: [ProjectActions.nodes.clone({ initiator, id })],
    })
  }

export const thunkChangeSelectedNodesColor =
  (colorId: ID): AppThunk =>
  (dispatch, getState) => {
    const { canvas } = getState()

    const { selectedNodes } = canvas.node

    if (!selectedNodes.length) {
      return
    }

    dispatch({
      type: ActionTypes.COMMIT_PROJECT_REQUEST,
      payload: [
        ...selectedNodes.map((id) =>
          ProjectActions.nodes.updateColor({ id, colorId })
        ),
      ],
    })
  }

export const thunkChangeNodesVariant =
  (variant: NodeVariant): AppThunk =>
  (dispatch, getState) => {
    const { canvas } = getState()

    const { selectedNodes } = canvas.node

    if (!selectedNodes.length) {
      return
    }

    dispatch({
      type: ActionTypes.COMMIT_PROJECT_REQUEST,
      payload: [
        ...selectedNodes.map((id) =>
          ProjectActions.nodes.updateVariant({ id, variant })
        ),
      ],
    })
  }

export const thunkDeleteSelectedNodes =
  (): AppThunk => (dispatch, getState) => {
    const { canvas } = getState()

    const { selectedNodes } = canvas.node

    if (!selectedNodes.length) {
      return
    }

    dispatch({
      type: ActionTypes.COMMIT_PROJECT_REQUEST,
      payload: [ProjectActions.nodes.delete({ ids: selectedNodes })],
    })

    dispatch({
      type: CanvasActionTypes.CLEAR_SELECTED_NODES,
    })
  }

export const thunkUpdateNodeNotes =
  (id: ID, notes: Notes): AppThunk =>
  (dispatch) => {
    dispatch(
      commitProjectRequest(ProjectActions.nodes.updateNotes({ id, notes }))
    )
  }

export const thunkUpdateNodeUrl =
  (id: ID, url: string): AppThunk =>
  (dispatch) => {
    dispatch(commitProjectRequest(ProjectActions.nodes.updateUrl({ id, url })))
  }

export const thunkChangeCollapseNode =
  (id: ID): AppThunk =>
  (dispatch, getState) => {
    const { collapsed } = migrationProjectSelector.getProjectNodeById(id)(
      getState()
    )

    dispatch({
      type: ActionTypes.COMMIT_PROJECT_REQUEST,
      payload: [
        {
          type: ProjectActions.nodes.changeCollapse.type,
          payload: { ids: [id], collapsed: !collapsed },
        },
      ],
    })
  }

export const thunkCopySelectedNodes =
  (copyType?: Partial<CopySelectNodeArgs>): AppThunk =>
  (_, getState) => {
    const state = getState()
    const {
      canvas: {
        node: { selectedNodes },
      },
      relations,
      manager: { project },
      library,
    } = state

    if (copyType?.copyTree) {
      const nodeId = selectedNodes[0]
      const allChildren = selectCompleteChildIds([nodeId])(state)
      const copyData = selectStoreToCopy(allChildren)({
        relations,
        project,
        library,
      })
      encodeAndCopyToClipboard([copyData], 'copyable_store')
      return
    }

    if (copyType?.shiftKey) {
      const allChildren = selectCompleteChildIds(selectedNodes)(state)
      const copyData = selectStoreToCopy(allChildren)({
        relations,
        project,
        library,
      })
      encodeAndCopyToClipboard([copyData], 'copyable_store')
      return
    }

    const shallowChildren = selectNodeShallowChildIds(selectedNodes)(state)
    const copyData = selectStoreToCopy(shallowChildren)({
      relations,
      project,
      library,
    })
    encodeAndCopyToClipboard([copyData], 'copyable_store')
  }

// The core attaches the pasted roots as the last children of the target: a
// page or a section.
export const pasteNodesAndArrows =
  ({ targetId, items }: { targetId: ID; items: CopyableStore[] }): AppThunk =>
  (dispatch) => {
    const store = items[0]
    if (!store) {
      return
    }

    const initiator = getId()
    dispatch(
      commitProjectRequest(
        CorePasteAction({
          data: store,
          initiator,
          target: targetId,
        })
      )
    )
  }
